import { renderLucideIcon } from '../utils/lucide-icons.js'

// Point sensible : cette méthode est la porte d'entrée du renderer.
// Elle doit reconnaître le brouillon simple quel que soit le chemin de réponse
// utilisé par le runtime : payload direct, data de step, ui imbriquée, ou objet
// display reconstitué. Si elle retourne null, le chat retombe sur les choix
// manuels du step et l'utilisateur voit seulement "Envoyer ou abandonner".
function normalizeEmailSimpleDraftUi(result) {
  var resolved = resolveEmailSimpleDraftSource(result)
  if (!resolved) return null

  var ui = resolved.ui
  var source = resolved.source
  var draft = normalizeDraft(ui.draft || source.draft || buildDraftFromFields(ui, source))
  if (!draft) return null

  var labels = ui.labels && typeof ui.labels === 'object' ? ui.labels : {}
  var actions = ui.actions && typeof ui.actions === 'object' ? ui.actions : {}
  var nextStep = ui.nextStep && typeof ui.nextStep === 'object' ? ui.nextStep : {}

  return {
    type: 'email-simple-draft',
    title: String(ui.title || 'Brouillon email'),
    recipientLabel: String(ui.recipientLabel || draft.recipient.label || draft.recipient.email || ''),
    recipient: draft.recipient,
    labels: {
      recipient: String(labels.recipient || 'Destinataire'),
      email: String(labels.email || 'Email'),
      subject: String(labels.subject || 'Objet'),
      body: String(labels.body || "Corps de l'email")
    },
    actions: {
      send: String(actions.send || 'Envoyer'),
      abandon: String(actions.abandon || actions.cancel || 'Abandonner')
    },
    nextStep: {
      key: String(nextStep.key || 'EMailSimpleSend'),
      label: String(nextStep.label || actions.send || 'Envoyer')
    },
    draft: draft
  }
}

// Méthode de rendu pure : elle ne lit pas l'état du widget et n'attache aucun
// événement. Toute valeur injectée dans le HTML est échappée ici pour éviter
// qu'un sujet ou un corps généré par LLM ne devienne du markup actif.
function renderEmailSimpleDraftUi(ui) {
  return (
    '<div class="wc-email-simple-draft-ui" data-ds-view="email-simple-draft">' +
      '<div class="wc-ds-capability wc-ds-capability-email">' +
        '<div class="wc-ds-capability-name">' + renderLucideIcon('mail') + '<span>@Plume</span></div>' +
        '<div class="wc-ds-capability-meta">' + escapeHtml(ui.title || 'Brouillon email') + '</div>' +
      '</div>' +
      '<form class="wc-email-simple-draft-form">' +
        '<div class="wc-email-simple-draft-head">' +
          '<div class="wc-email-simple-draft-title">' + escapeHtml(ui.title || 'Brouillon email') + '</div>' +
          '<div class="wc-email-simple-draft-recipient">' +
            '<span>' + escapeHtml(ui.labels.recipient || 'Destinataire') + '</span>' +
            '<strong>' + escapeHtml(renderRecipient(ui)) + '</strong>' +
          '</div>' +
        '</div>' +
        '<label class="wc-email-simple-draft-field">' +
          '<span>' + escapeHtml(ui.labels.subject || 'Objet') + '</span>' +
          '<input type="text" data-email-simple-draft-subject value="' + escapeAttributeValue(ui.draft.subject || '') + '" maxlength="240">' +
        '</label>' +
        '<label class="wc-email-simple-draft-field">' +
          '<span>' + escapeHtml(ui.labels.body || "Corps de l'email") + '</span>' +
          '<textarea data-email-simple-draft-body rows="10" maxlength="6000">' + escapeHtml(ui.draft.body || '') + '</textarea>' +
        '</label>' +
        '<div class="wc-email-simple-draft-actions">' +
          '<button type="button" class="wc-email-simple-draft-abandon" data-email-simple-draft-abandon>' + renderLucideIcon('x') + '<span>' + escapeHtml(ui.actions.abandon || 'Abandonner') + '</span></button>' +
          '<button type="submit" class="wc-email-simple-draft-send">' + renderLucideIcon('send') + '<span>' + escapeHtml(ui.actions.send || 'Envoyer') + '</span></button>' +
        '</div>' +
      '</form>' +
    '</div>'
  )
}

// Méthode de binding : elle traduit les deux actions visibles du formulaire en
// payload applicatif. Le submit correspond toujours à l'envoi explicite ; le
// bouton secondaire correspond toujours à l'abandon.
function bindEmailSimpleDraftUi(el, ui, handlers) {
  if (!el || !ui) return
  var form = el.querySelector('.wc-email-simple-draft-form')
  var abandonButton = el.querySelector('[data-email-simple-draft-abandon]')

  if (form) {
    form.addEventListener('submit', function (event) {
      event.preventDefault()
      if (!handlers || typeof handlers.onConfirm !== 'function') return
      handlers.onConfirm(buildPayload(el, ui, 'send'))
    })
  }

  if (abandonButton) {
    abandonButton.addEventListener('click', function () {
      if (!handlers || typeof handlers.onConfirm !== 'function') return
      handlers.onConfirm(buildPayload(el, ui, 'abandon'))
    })
  }
}

// Point sensible : ce payload est consommé par chat-widget.js puis par
// EMailSimpleSend. Les noms emailSimpleDraftPatch et emailSimpleSendAction sont
// contractuels côté backend ; les changer casserait l'envoi simple.
function buildPayload(el, ui, action) {
  return {
    nextStepKey: ui.nextStep.key || 'EMailSimpleSend',
    label: action === 'send' ? ui.actions.send : ui.actions.abandon,
    action: action,
    draft: {
      subject: readValue(el, '[data-email-simple-draft-subject]').slice(0, 240),
      body: readValue(el, '[data-email-simple-draft-body]').slice(0, 6000)
    }
  }
}

// Méthode de résolution : elle parcourt les variantes de shape que le runtime
// peut renvoyer. On garde aussi la source originale pour retrouver draft,
// recipient ou fields lorsque l'UI ne les porte pas directement.
function resolveEmailSimpleDraftSource(result) {
  if (!result || typeof result !== 'object') return null

  var stepResult = result.stepResult && typeof result.stepResult === 'object' ? result.stepResult : null
  var stepData = stepResult && stepResult.data && typeof stepResult.data === 'object' ? stepResult.data : null
  var candidates = [
    { ui: result, source: result },
    { ui: result.ui, source: result },
    { ui: result.emailSimpleDraftUi, source: result },
    stepResult ? { ui: stepResult, source: stepResult } : null,
    stepData ? { ui: stepData, source: stepData } : null,
    stepData ? { ui: stepData.ui, source: stepData } : null
  ]

  for (var index = 0; index < candidates.length; index += 1) {
    var candidate = candidates[index]
    if (!candidate || !candidate.ui || typeof candidate.ui !== 'object') continue
    if (String(candidate.ui.type || '').toLowerCase() === 'email-simple-draft') {
      return candidate
    }
  }

  return null
}

// Méthode de compatibilité : certains payloads historiques portent les champs
// éditables dans fields plutôt que dans draft. On reconstruit alors un draft
// minimal pour éviter de perdre le rendu.
function buildDraftFromFields(ui, source) {
  var fields = Array.isArray(ui.fields) ? ui.fields : Array.isArray(source.fields) ? source.fields : []
  if (!fields.length) return null

  return {
    subject: findFieldValue(fields, 'subject'),
    body: findFieldValue(fields, 'body'),
    recipient: ui.recipient || source.recipient || (source.draft && source.draft.recipient)
  }
}

// Méthode utilitaire : récupère une valeur de champ normalisée depuis le contrat
// fields de EMailSimpleDraft.
function findFieldValue(fields, key) {
  var field = fields.find(function (candidate) {
    return candidate && candidate.key === key
  })
  return field ? String(field.value || '') : ''
}

// Méthode de normalisation métier : un brouillon sans destinataire exploitable
// n'est pas rendable, car le bouton Envoyer doit toujours cibler un contact.
function normalizeDraft(value) {
  if (!value || typeof value !== 'object') return null
  var recipient = normalizeRecipient(value.recipient)
  if (!recipient) return null

  return {
    subject: String(value.subject || '').trim(),
    body: String(value.body || '').trim(),
    recipient: recipient
  }
}

// Méthode de normalisation du destinataire : on conserve seulement les champs
// utiles au rendu, le backend gardant la source métier complète en session.
function normalizeRecipient(value) {
  if (!value || typeof value !== 'object') return null
  return {
    label: String(value.label || '').trim(),
    email: String(value.email || '').trim(),
    contactId: String(value.contactId || '').trim()
  }
}

// Méthode d'affichage : privilégie "Nom <email>" pour que l'utilisateur puisse
// contrôler le destinataire avant d'envoyer.
function renderRecipient(ui) {
  var label = ui.recipientLabel || ui.recipient.label || ''
  var email = ui.recipient.email || ''
  if (label && email) return label + ' <' + email + '>'
  return label || email
}

// Méthode de lecture formulaire : trim volontaire pour éviter les espaces
// accidentels en début/fin de sujet et de corps.
function readValue(el, selector) {
  var input = el.querySelector(selector)
  return input ? String(input.value || '').trim() : ''
}

// Méthode d'échappement d'attribut : utilisée pour la value de l'objet.
function escapeAttributeValue(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Méthode d'échappement HTML : utilisée pour le corps dans textarea et tous les
// libellés visibles.
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export { bindEmailSimpleDraftUi, normalizeEmailSimpleDraftUi, renderEmailSimpleDraftUi }
